<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AgentConfigAPI from 'dashboard/api/agentConfig';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const agentConfigs = ref([]);
const isLoading = ref(false);
const searchQuery = ref('');
const showPublishedModal = ref(false);
const publishedJsonContent = ref('');
const selectedAgentName = ref('');

const fetchAgentConfigs = async () => {
  try {
    isLoading.value = true;
    const response = await AgentConfigAPI.get();
    agentConfigs.value = response.data.payload.agent_configs || [];
  } catch (error) {
    useAlert(t('AGENT_CONFIG.LIST.EMPTY'));
  } finally {
    isLoading.value = false;
  }
};

const filteredAgentConfigs = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  if (!query) return agentConfigs.value;
  return agentConfigs.value.filter(
    config =>
      config.nome_empresa?.toLowerCase().includes(query) ||
      config.nome_departamento?.toLowerCase().includes(query)
  );
});

const openCreate = () => {
  router.push(accountScopedRoute('agent_configs_new'));
};

const openEdit = config => {
  router.push(
    accountScopedRoute('agent_configs_edit', { agentConfigId: config.id })
  );
};

const publishAgent = async config => {
  try {
    await AgentConfigAPI.publish(config.id);
    useAlert(t('AGENT_CONFIG.PUBLISHED_SUCCESS'));
    await fetchAgentConfigs();
  } catch (error) {
    useAlert('Erro ao publicar agente.');
  }
};

const viewPublishedJson = async config => {
  try {
    const response = await AgentConfigAPI.getPublished(config.id);
    publishedJsonContent.value = JSON.stringify(response.data, null, 2);
    selectedAgentName.value = config.nome_empresa;
    showPublishedModal.value = true;
  } catch (error) {
    useAlert('Erro ao carregar JSON publicado.');
  }
};

const copyPublishedJson = async () => {
  try {
    await copyTextToClipboard(publishedJsonContent.value);
    useAlert('JSON copiado para a área de transferência!');
  } catch (error) {
    useAlert('Falha ao copiar JSON.');
  }
};

const deleteAgent = async config => {
  if (!window.confirm(t('AGENT_CONFIG.DELETE_CONFIRM'))) return;
  try {
    await AgentConfigAPI.delete(config.id);
    useAlert('Agente excluído com sucesso.');
    await fetchAgentConfigs();
  } catch (error) {
    useAlert('Erro ao excluir agente.');
  }
};

onMounted(() => {
  fetchAgentConfigs();
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AGENT_CONFIG.HEADER')"
        :description="$t('AGENT_CONFIG.DESCRIPTION')"
        :search-placeholder="$t('AGENT_CONFIG.LIST.TABLE.NAME')"
      >
        <template #actions>
          <Button
            icon="i-lucide-plus"
            :label="$t('AGENT_CONFIG.HEADER_BTN_TXT')"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        v-if="!isLoading && filteredAgentConfigs.length === 0"
        class="flex flex-col items-center justify-center p-12 text-center bg-n-surface-1 border border-n-weak rounded-xl mt-4"
      >
        <div
          class="size-16 rounded-full bg-n-brand/10 text-n-brand flex items-center justify-center mb-4"
        >
          <Icon icon="i-lucide-bot" class="size-8" />
        </div>
        <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
          {{ $t('AGENT_CONFIG.LIST.EMPTY') }}
        </h3>
        <p class="text-sm text-n-slate-11 max-w-md mb-6">
          Configure a Isa Leal para automatizar o atendimento dos seus clientes
          via n8n.
        </p>
        <Button
          icon="i-lucide-plus"
          :label="$t('AGENT_CONFIG.HEADER_BTN_TXT')"
          @click="openCreate"
        />
      </div>

      <div
        v-else
        class="overflow-x-auto border border-n-weak rounded-xl mt-4 bg-n-surface-1"
      >
        <table class="w-full text-left text-sm text-n-slate-12">
          <thead
            class="bg-n-surface-2 border-b border-n-weak text-xs uppercase font-medium text-n-slate-11"
          >
            <tr>
              <th class="px-6 py-3.5">
                {{ $t('AGENT_CONFIG.LIST.TABLE.NAME') }}
              </th>
              <th class="px-6 py-3.5">
                {{ $t('AGENT_CONFIG.LIST.TABLE.STATUS') }}
              </th>
              <th class="px-6 py-3.5">
                {{ $t('AGENT_CONFIG.LIST.TABLE.VERSION') }}
              </th>
              <th class="px-6 py-3.5">
                {{ $t('AGENT_CONFIG.LIST.TABLE.PUBLISHED_AT') }}
              </th>
              <th class="px-6 py-3.5 text-right">
                {{ $t('AGENT_CONFIG.LIST.TABLE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="config in filteredAgentConfigs"
              :key="config.id"
              class="hover:bg-n-alpha-1 transition-colors"
            >
              <td class="px-6 py-4 font-medium flex items-center gap-3">
                <div
                  class="size-9 rounded-lg bg-n-brand/10 text-n-brand flex items-center justify-center font-bold text-sm flex-shrink-0"
                >
                  <Icon icon="i-lucide-bot" class="size-5" />
                </div>
                <div>
                  <div class="font-semibold text-n-slate-12">
                    {{ config.nome_empresa }}
                  </div>
                  <div class="text-xs text-n-slate-11">
                    {{ config.nome_departamento || 'Geral' }}
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                  :class="
                    config.status === 'published'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-500/20'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-500/20'
                  "
                >
                  <span
                    class="size-1.5 rounded-full mr-1.5"
                    :class="
                      config.status === 'published'
                        ? 'bg-emerald-500'
                        : 'bg-amber-500'
                    "
                  />
                  {{
                    config.status === 'published'
                      ? $t('AGENT_CONFIG.LIST.STATUS_PUBLISHED')
                      : $t('AGENT_CONFIG.LIST.STATUS_DRAFT')
                  }}
                </span>
              </td>
              <td class="px-6 py-4 font-mono text-xs text-n-slate-11">
                v{{ config.version || 1 }}
              </td>
              <td class="px-6 py-4 text-xs text-n-slate-11">
                {{
                  config.published_at
                    ? new Date(config.published_at).toLocaleString()
                    : '-'
                }}
              </td>
              <td class="px-6 py-4 text-right space-x-1">
                <Button
                  ghost
                  slate
                  sm
                  icon="i-lucide-code"
                  title="Ver JSON do n8n"
                  @click="viewPublishedJson(config)"
                />
                <Button
                  ghost
                  emerald
                  sm
                  icon="i-lucide-upload-cloud"
                  :title="$t('AGENT_CONFIG.LIST.PUBLISH')"
                  @click="publishAgent(config)"
                />
                <Button
                  ghost
                  slate
                  sm
                  icon="i-lucide-pencil"
                  :title="$t('AGENT_CONFIG.LIST.EDIT')"
                  @click="openEdit(config)"
                />
                <Button
                  ghost
                  ruby
                  sm
                  icon="i-lucide-trash-2"
                  :title="$t('AGENT_CONFIG.LIST.DELETE')"
                  @click="deleteAgent(config)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Modal JSON Publicado (n8n ready) -->
      <div
        v-if="showPublishedModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
      >
        <div
          class="bg-n-surface-1 border border-n-weak rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden"
        >
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-n-weak bg-n-surface-2"
          >
            <div class="flex items-center gap-2">
              <Icon icon="i-lucide-code" class="size-5 text-n-brand" />
              <h3 class="font-bold text-base text-n-slate-12">
                Payload JSON para n8n — {{ selectedAgentName }}
              </h3>
            </div>
            <div class="flex items-center gap-2">
              <Button
                ghost
                sm
                icon="i-lucide-copy"
                label="Copiar JSON"
                @click="copyPublishedJson"
              />
              <Button
                ghost
                slate
                sm
                icon="i-lucide-x"
                @click="showPublishedModal = false"
              />
            </div>
          </div>
          <div
            class="p-4 flex-1 overflow-auto bg-slate-950 font-mono text-xs text-emerald-400"
          >
            <pre>{{ publishedJsonContent }}</pre>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
